/**
 * Void / refund request helpers: Indonesian labels, tolerant field readers for
 * the approval row, client-side validation and error-message mapping.
 *
 * The cashier never voids or refunds directly — they submit a request and a
 * supervisor approves it in the BO, which executes it. See plan §3/§5.
 */
import type { ApiError } from '@/types/api';
import type {
  ApprovalActor,
  ApprovalKind,
  RefundableLine,
  SaleApprovalRequest,
} from '@/types/sale';

export const REASON_MIN = 10;
export const REASON_MAX = 500;

export const KIND_LABEL: Record<ApprovalKind, string> = {
  VOID: 'Void',
  FULL_REFUND: 'Refund penuh',
  PARTIAL_REFUND: 'Refund sebagian',
};

export const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Menunggu',
  APPROVED: 'Disetujui',
  CONSUMED: 'Selesai',
  REJECTED: 'Ditolak',
  EXPIRED: 'Kedaluwarsa',
  WITHDRAWN: 'Ditarik',
};

export const STATUS_CHIP_CLASS: Record<string, string> = {
  PENDING: 'bg-amber-100 text-amber-700',
  APPROVED: 'bg-blue-100 text-blue-700',
  CONSUMED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
  EXPIRED: 'bg-slate-100 text-slate-600',
  WITHDRAWN: 'bg-slate-100 text-slate-600',
};

export function kindLabel(kind: string | null | undefined): string {
  return (kind && KIND_LABEL[kind as ApprovalKind]) || 'Refund';
}

export function statusLabel(status: string | null | undefined): string {
  return (status && STATUS_LABEL[status]) || String(status ?? '—');
}

/** The request's reason — top-level `reason`, else the legacy column. */
export function approvalReason(r: SaleApprovalRequest): string {
  return r.reason ?? r.requestPayloadReason ?? '';
}

/** Requested amount — `totalRefundAmount` (BigInt; number or string), else the sale total. */
export function approvalAmount(r: SaleApprovalRequest): number {
  const raw = r.totalRefundAmount ?? r.sale?.totalAmount ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function actorNameOf(a: string | ApprovalActor | null | undefined): string | null {
  return a && typeof a === 'object' ? (a.name ?? a.email ?? null) : null;
}

export function requesterName(r: SaleApprovalRequest): string | null {
  return r.requestedByName ?? actorNameOf(r.requestedBy);
}

export function approverName(r: SaleApprovalRequest): string | null {
  return r.approvedByName ?? actorNameOf(r.approvedBy);
}

/** Does `r` belong to this sale? Matches by uuid or by invoice. */
export function requestMatchesSale(
  r: SaleApprovalRequest,
  sale: { id?: string | null; invoiceId?: string | null },
): boolean {
  if (sale.id && r.saleId && String(r.saleId) === String(sale.id)) return true;
  if (sale.invoiceId && r.sale?.invoiceId && r.sale.invoiceId === sale.invoiceId) return true;
  return false;
}

// ── Validation ─────────────────────────────────────────────────────────────

export interface RequestDraft {
  kind: ApprovalKind | null;
  reason: string;
  /** PARTIAL_REFUND only — qty per saleItemId. */
  lineQty?: Record<string, number>;
  /** PARTIAL_REFUND only — the refundable lines (for the unusedQty cap). */
  lines?: RefundableLine[];
}

/**
 * Client-side gate mirroring the server's Joi: reason 10–500 chars (trimmed);
 * a partial refund needs at least one line, each qty within 1..unusedQty.
 * Returns a list of Indonesian error strings (empty = valid).
 */
export function validateRequest(d: RequestDraft): string[] {
  const errors: string[] = [];
  if (!d.kind) errors.push('Pilih jenis permintaan.');
  const len = d.reason.trim().length;
  if (len < REASON_MIN) errors.push(`Alasan minimal ${REASON_MIN} karakter.`);
  if (len > REASON_MAX) errors.push(`Alasan maksimal ${REASON_MAX} karakter.`);
  if (d.kind === 'PARTIAL_REFUND') {
    const picks = Object.entries(d.lineQty ?? {}).filter(([, q]) => q > 0);
    if (picks.length === 0) errors.push('Pilih minimal satu tiket untuk direfund.');
    for (const [id, q] of picks) {
      const line = d.lines?.find((l) => l.saleItemId === id);
      if (!line) errors.push('Baris tiket tidak dikenal.');
      else if (!Number.isInteger(q) || q > line.unusedQty)
        errors.push(`${line.productName}: maksimal ${line.unusedQty} tiket belum dipakai.`);
    }
  }
  return errors;
}

/** Refund estimate for a partial pick: pro-rata of each line's refundable amount. */
export function partialRefundTotal(lines: RefundableLine[], lineQty: Record<string, number>): number {
  let total = 0;
  for (const l of lines) {
    const q = lineQty[l.saleItemId] ?? 0;
    if (q <= 0) continue;
    const per =
      l.unusedQty > 0 && Number.isFinite(l.refundableAmount)
        ? l.refundableAmount / l.unusedQty
        : l.unitPrice;
    total += Math.round(per * q);
  }
  return total;
}

// ── Errors ─────────────────────────────────────────────────────────────────

export const TICKET_USED_MESSAGE =
  'Sebagian tiket sudah dipakai — void / refund penuh tidak bisa. Ajukan refund sebagian untuk tiket yang belum dipakai.';

export function isTicketUsed(err: unknown): boolean {
  return (err as ApiError | null)?.errorCode === 'TICKET_ALREADY_USED';
}

/** A 409 that is not "ticket used" — almost always an open request already exists. */
export function isPendingConflict(err: unknown): boolean {
  const e = err as ApiError | null;
  return Boolean(e && e.code === 409 && !isTicketUsed(e));
}

/** Map a request/withdraw failure to an Indonesian message for the cashier. */
export function approvalErrorMessage(err: unknown): string {
  const e = err as ApiError | null;
  if (!e) return 'Terjadi kesalahan.';
  if (e.isNetwork) return 'Tidak dapat terhubung ke server.';
  const code = (e.errorCode ?? '').toUpperCase();
  if (code === 'TICKET_ALREADY_USED') return TICKET_USED_MESSAGE;
  if (code === 'SALE_NOT_OWNED_BY_CALLER') return 'Transaksi ini bukan milik Anda — tidak dapat diajukan.';
  if (code.includes('DISABLED') || code === 'FEATURE_OFF')
    return 'Fitur refund sedang dinonaktifkan. Hubungi supervisor.';
  if (code === 'PERMISSION_DENIED' || e.code === 403)
    return 'Anda tidak memiliki izin untuk pengajuan ini.';
  if (e.code === 409)
    return e.message && e.message !== 'Request failed'
      ? e.message
      : 'Sudah ada permintaan yang menunggu persetujuan untuk transaksi ini.';
  return e.message || 'Terjadi kesalahan.';
}

/** One idempotency key per dialog submission attempt. */
export function newIdempotencyKey(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
